import { useState, useEffect, useRef } from 'react';
import * as nsfwjs from 'nsfwjs';
import Swal from 'sweetalert2';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

function TweetForm({ tweet, setTweet, handleTweet, media, setMedia }) {
  const [loading, setLoading] = useState(false);
  const [mediaSizes, setMediaSizes] = useState({}); // Para controlar tamaños
  const modelRef = useRef(null);

  const IMAGE_LIMIT_MB = 30;
  const VIDEO_LIMIT_MB = 50;

  useEffect(() => {
    const loadModel = async () => {
      modelRef.current = await nsfwjs.load();
    };
    loadModel();
  }, []);

  const isImageNSFW = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = async () => {
          const predictions = await modelRef.current.classify(img);
          const nsfw = predictions.find(p =>
            ['Porn', 'Hentai', 'Sexy'].includes(p.className) && p.probability > 0.7
          );
          resolve(!!nsfw);
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const getTotalSizeMB = (type) => {
    return Object.entries(mediaSizes)
      .filter(([url, info]) => type === "video" ? info.type === "video" : info.type === "image")
      .reduce((acc, [, info]) => acc + info.fileSize, 0) / (1024 * 1024);
  };

  const getMediaInfo = (item) => {
  if (!item) return { url: "", type: "image" };

  // Si es un string (Cloudinary antiguo)
  if (typeof item === "string") {
    const isVideo = item.match(/\.(mp4|mov|avi|webm)$/i);
    return { url: item, type: isVideo ? "video" : "image" };
  }

  // Si es un objeto { url, type } (Firebase nuevo)
  if (typeof item === "object" && item.url && item.type) {
    return { url: item.url, type: item.type };
  }

  // fallback
  return { url: "", type: "image" };
};


const uploadToFirebase = async (files) => {
  if (!files || files.length === 0) return;

  const newMedia = [...media]; // Array de objetos { url, type }
  const newSizes = { ...mediaSizes }; // Para controlar tamaño y tipo

  setLoading(true);

  for (let file of files) {
    const isVideo = file.type.startsWith("video");

    // Contar videos e imágenes actuales
    const currentVideoCount = newMedia.filter((m) => m.type === "video").length;
    const currentImageCount = newMedia.filter((m) => m.type === "image").length;

    // Validar límites
    if (isVideo && currentVideoCount >= 3) {
      Swal.fire("Máximo 3 videos", "Ya alcanzaste el límite de videos por tweet.", "warning");
      continue;
    }
    if (!isVideo && currentImageCount >= 5) {
      Swal.fire("Máximo 5 imágenes", "Ya alcanzaste el límite de imágenes por tweet.", "warning");
      continue;
    }

    // Validar tamaño total
    const totalMB = getTotalSizeMB(isVideo ? "video" : "image") + file.size / (1024 * 1024);
    if (!isVideo && totalMB > IMAGE_LIMIT_MB) {
      Swal.fire("Límite excedido", `El tamaño total de imágenes no puede superar ${IMAGE_LIMIT_MB}MB.`, "error");
      continue;
    }
    if (isVideo && totalMB > VIDEO_LIMIT_MB) {
      Swal.fire("Límite excedido", `El tamaño total de videos no puede superar ${VIDEO_LIMIT_MB}MB.`, "error");
      continue;
    }

    // Validar NSFW para imágenes
    if (!isVideo) {
      const nsfw = await isImageNSFW(file);
      if (nsfw) {
        Swal.fire("Contenido bloqueado", "Esta imagen contiene contenido no permitido (NSFW).", "error");
        continue;
      }
    }

    try {
      // Crear ruta única en Firebase Storage
      const filePath = `tweets/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);

      // Subir archivo
      const snapshot = await uploadBytes(storageRef, file);

      // Obtener URL pública
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Guardar en estado como objeto { url, type }
      const mediaObj = {
        url: downloadURL,
        type: isVideo ? "video" : "image"
      };

      newMedia.push(mediaObj);
      newSizes[downloadURL] = { fileSize: file.size, type: mediaObj.type };
      setMedia(newMedia);
      setMediaSizes(newSizes);
    } catch (err) {
      console.error("Error al subir archivo a Firebase:", err);
      Swal.fire("Error", "No se pudo subir el archivo.", "error");
    }
  }

  setLoading(false);
};



  const imagePercentage = Math.min((getTotalSizeMB("image") / IMAGE_LIMIT_MB) * 100, 100);
  const videoPercentage = Math.min((getTotalSizeMB("video") / VIDEO_LIMIT_MB) * 100, 100);

  return (
    <>
      <textarea
        className="w-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-lg p-2 mb-3 resize-none"
        rows="3"
        placeholder="¿Qué está pasando?"
        value={tweet}
        onChange={(e) => setTweet(e.target.value)}
      />

      {/* Barra de tamaño */}
      {media.length > 0 && (
        <div className="mb-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Imágenes: {getTotalSizeMB("image").toFixed(1)} / {IMAGE_LIMIT_MB} MB
          </p>
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-3 rounded-full mb-2">
            <div
              className={`h-3 rounded-full transition-all ${imagePercentage >= 100 ? "bg-red-500" : "bg-blue-500"}`}
              style={{ width: `${imagePercentage}%` }}
            ></div>
          </div>

          <p className="text-sm text-gray-700 dark:text-gray-300">
            Videos: {getTotalSizeMB("video").toFixed(1)} / {VIDEO_LIMIT_MB} MB
          </p>
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-3 rounded-full">
            <div
              className={`h-3 rounded-full transition-all ${videoPercentage >= 100 ? "bg-red-500" : "bg-green-500"}`}
              style={{ width: `${videoPercentage}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <label className="group relative inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors cursor-pointer">
          Subir archivo
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => uploadToFirebase([...e.target.files])}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>

        <label className="group relative inline-flex items-center justify-center px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium shadow-sm hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 transition-colors cursor-pointer">
          Usar cámara
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => uploadToFirebase([e.target.files[0]])}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
      </div>

      {loading && <p className="text-sm text-gray-500 my-2">Subiendo archivo...</p>}

      {/* Previews */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 gap-2 my-3">
          {media.map((item, idx) => {
            const { url, type } = getMediaInfo(item);
            return (
              <div key={idx} className="relative">
                {type === "video" ? (
                  <video controls className="rounded h-48 w-full object-cover">
                    <source src={url} type="video/mp4" />
                  </video>
                ) : (
                  <img src={url} alt={`media-${idx}`} className="rounded h-48 w-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMedia((prev) => prev.filter((_, i) => i !== idx));
                    setMediaSizes((prev) => {
                      const updated = { ...prev };
                      delete updated[url];
                      return updated;
                    });
                  }}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-xs font-bold shadow"
                  title="Eliminar"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}


      <div className="flex justify-end pr-4">
        <button
          onClick={() => handleTweet(media)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Tweetear
        </button>
      </div>

      <hr className="my-5" />
    </>
  );
}

export default TweetForm;
